import { useState } from "react";
import { getLoanProducts } from "../../sdk/products/products";
import { useQuery } from "react-query";
import { useError } from "../../context/ErrorProvider";
import { getAllUsers } from "../../sdk/users/users";
import { getBranches } from "../../sdk/branch/branch";
import { useNavigate } from "react-router-dom";
import { downloadGeneratedReport } from "../../sdk/reports/reports";
import { downloadExportFile } from "../../utils/export";
import Loading from "../../shared/Loading";

const Reports = () => {
  const navigate = useNavigate();
  const [loanProducts, setLoanProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const { showError } = useError();
  const [reportDetails, setReportDetails] = useState({
    type: "",
    branchId: "",
    staffId: "",
    productId: "",
    startDate: "",
    endDate: "",
    status: "",
    page: 1,
    limit: 20,
    export: false,
  });

  useQuery({
    queryKey: ["loan-products"],
    queryFn: async () => {
      const response = await getLoanProducts();
      return response;
    },
    initialData: [],
    onSuccess: (data) => {
      setLoanProducts(data?.data);
    },
  });

  useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await getAllUsers();
      return response.data.data;
    },
    onSuccess: (data) => {
      setUsers(data.items);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { data: branches } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getBranches(),
  });

  const handleSaveReportParameters = () => {
    if (reportDetails?.type) {
      if (reportDetails?.type === "loan_analysis_by_officer") {
        localStorage.setItem("report_filters", JSON.stringify(reportDetails));
        navigate(`/dashboard/loan-reports/loan-analysis`);
      } else {
        localStorage.setItem("report_filters", JSON.stringify(reportDetails));
        navigate(`/dashboard/reports/${reportDetails?.type}`);
      }
    } else {
      showError("No report type selected");
    }
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["download reports"],
    queryFn: async () => {
      let reportDetails = localStorage.getItem("report_filters")
        ? JSON.parse(localStorage.getItem("report_filters"))
        : {};
      const response = await downloadGeneratedReport(
        reportDetails?.page,
        reportDetails?.limit,
        reportDetails?.startDate,
        reportDetails?.endDate,
        reportDetails?.type,
        reportDetails?.branchId,
        reportDetails?.productId,
        reportDetails?.status,
        true,
      );
      return response;
    },
    enabled: false,
    onSuccess: (data) => {
      downloadExportFile(data, `${reportDetails?.type}.csv`);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleDownloadFile = () => {
    if (reportDetails.type) {
      refetch();
    } else {
      showError("No report type selected");
    }
  };

  return (
    <div className="w-full pl-[10px]">
      <p className="text-[#222222] mt-[10px] text-[27px] font-bold">Reports</p>
      <div className="w-full bg-white my-[20px] h-[450px] p-[20px]">
        <p className="text-[#222222] font-bold text-[20px]">
          Generate a report
        </p>
        <div className="flex items-start my-[20px] justify-between">
          {/* Report Type */}
          <div className="w-[48%] flex flex-col gap-[5px]">
            <label htmlFor="reportType">Report Type</label>
            <select
              id="reportType"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              value={reportDetails.type}
              onChange={(e) =>
                setReportDetails({
                  ...reportDetails,
                  type: e.target.value,
                })
              }
            >
              <option value="">-- Select Report Type --</option>
              <option value="loan_listing">Loan Listing</option>
              <option value="loan_disbursements_list">
                Loan Disbursement List
              </option>
              <option value="loan_repayments_list">Loan Repayment List</option>
              <option value="installments_failing_due">
                Installment Failing Due
              </option>
              <option value="installments_in_arrears">
                Installment In Arrears
              </option>
              <option value="portfolio_at_risk">Portfolio At Risk</option>
              <option value="loan_analysis_by_officer">
                Loan Analysis By Officer
              </option>
            </select>
          </div>

          {/* Branch */}
          <div className="w-[48%] flex flex-col gap-[5px]">
            <label htmlFor="branch">Branch</label>
            <select
              id="branch"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              value={reportDetails.branchId}
              onChange={(e) =>
                setReportDetails({
                  ...reportDetails,
                  branchId: e.target.value,
                })
              }
            >
              <option value="">-- Select Branch --</option>
              {branches?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-start my-[20px] justify-between">
          {/* Staff/User */}
          <div className="w-[48%] flex flex-col gap-[5px]">
            <label htmlFor="staff">Staff or User</label>
            <select
              id="staff"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              value={reportDetails.staffId}
              onChange={(e) =>
                setReportDetails({
                  ...reportDetails,
                  staffId: e.target.value,
                })
              }
            >
              <option value="">-- Select Staff or User --</option>
              {users?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstname} {u.lastname}
                </option>
              ))}
            </select>
          </div>

          <div className="w-[48%] flex flex-col gap-[5px]">
            <label htmlFor="loanProduct">Loan Product</label>
            <select
              id="loanProduct"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              value={reportDetails.productId}
              onChange={(e) =>
                setReportDetails({
                  ...reportDetails,
                  productId: e.target.value,
                })
              }
            >
              <option value="">-- Select Loan Product --</option>
              {loanProducts?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-start my-[20px] justify-between">
          {/* From Date */}
          <div className="w-[48%] flex flex-col gap-[5px]">
            <label>From Date</label>
            <input
              type="date"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              onChange={(e) =>
                setReportDetails({
                  ...reportDetails,
                  startDate: e.target.value,
                })
              }
            />
          </div>

          {/* To Date */}
          <div className="w-[48%] flex flex-col gap-[5px]">
            <label>To Date</label>
            <input
              type="date"
              className="h-[45px] w-full text-[14px] rounded-[1px] px-[10px] border border-[#F3F3F3] bg-[#F3F3F3] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              onChange={(e) =>
                setReportDetails({ ...reportDetails, endDate: e.target.value })
              }
            />
          </div>
        </div>

        <div className="w-full flex items-center gap-[20px] justify-end">
          <button
            disabled={isFetching}
            onClick={handleDownloadFile}
            className="bg-[#F3F3F3] flex items-center justify-center gap-[5px] rounded-[30px] h-[40px] shadow-md text-[#000000] text-[15px] px-[20px]"
          >
            {isFetching && <Loading />}
            Export as CSV
          </button>
          <button
            onClick={handleSaveReportParameters}
            className="bg-[#000000] rounded-[30px] h-[40px] text-white text-[15px] px-[20px]"
          >
            Generate Report
          </button>
        </div>
      </div>
      <div className="h-[60px] flex items-center px-[10px] py-[5px] bg-[#FFDEDE]">
        <p className="text-[#000000] text-[15px] italic">
          Report generation may take a little longer depending on the data
          volume. Please wait while we process your request. You will be
          notified once the report is ready.
        </p>
      </div>
    </div>
  );
};

export default Reports;
